import React, { useEffect, useState } from "react";
import { ArrowLeft, Cpu, MemoryStick, HardDrive, Activity, Server, RefreshCw } from "lucide-react";
import { Toaster } from "sonner";
import { fetchSystemLoad } from "@/lib/api";

const C = {
    bg: "#0B0F14", panel: "#0F1620", border: "#1B2A3A", text: "#E6EDF6",
    sub: "#A7B4C6", dim: "#6F7D92", accent: "#2EE6D6", bull: "#2BE38B",
    warn: "#FFB020", bear: "#FF5C6C",
};

const gb = (b) => (b ? `${(b / 1e9).toFixed(2)} GB` : "0 GB");
const pctColor = (p) => (p == null ? C.dim : p < 60 ? C.bull : p < 85 ? C.warn : C.bear);

const fmtUptime = (s) => {
    if (s == null) return "—";
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
};

const Bar = ({ pct }) => (
    <div className="h-2 w-full rounded-full overflow-hidden" style={{ background: "#13202D" }}>
        <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(100, Math.max(0, pct || 0))}%`, background: pctColor(pct) }} />
    </div>
);

const Gauge = ({ label, pct, sub }) => {
    const p = Math.min(100, Math.max(0, pct || 0));
    const R = 46, CIRC = 2 * Math.PI * R;
    return (
        <div className="panel p-4 flex flex-col items-center" data-testid={`sys-gauge-${label}`}>
            <div className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: C.sub }}>{label}</div>
            <div className="relative" style={{ width: 120, height: 120 }}>
                <svg width="120" height="120" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r={R} fill="none" stroke="#13202D" strokeWidth="10" />
                    <circle cx="60" cy="60" r={R} fill="none" stroke={pctColor(p)} strokeWidth="10"
                        strokeLinecap="round" strokeDasharray={CIRC}
                        strokeDashoffset={CIRC * (1 - p / 100)} transform="rotate(-90 60 60)"
                        style={{ transition: "stroke-dashoffset .4s ease, stroke .4s ease" }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="font-mono text-2xl" style={{ color: C.text }}>{pct == null ? "—" : `${p.toFixed(0)}%`}</div>
                </div>
            </div>
            {sub && <div className="text-[11px] font-mono mt-1" style={{ color: C.dim }}>{sub}</div>}
        </div>
    );
};

const ResRow = ({ icon: Icon, label, pct, detail }) => (
    <div className="panel p-4" data-testid={`sys-res-${label}`}>
        <div className="flex items-center gap-2 mb-2">
            <Icon size={14} color={C.accent} />
            <span className="text-[10px] uppercase tracking-[0.16em]" style={{ color: C.sub }}>{label}</span>
            <span className="ml-auto font-mono text-sm" style={{ color: pctColor(pct) }}>{pct == null ? "—" : `${pct.toFixed(1)}%`}</span>
        </div>
        <Bar pct={pct} />
        <div className="text-[11px] font-mono mt-1.5" style={{ color: C.dim }}>{detail}</div>
    </div>
);

export default function ServerLoadPage({ onBack }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    const load = async () => {
        try { setData(await fetchSystemLoad()); setErr(null); }
        catch (e) { setErr(e?.response?.data?.detail || e.message); }
    };
    useEffect(() => { load(); const id = setInterval(load, 2000); return () => clearInterval(id); }, []);

    const cpu = data?.cpu || {};
    const mem = data?.memory || {};
    const swap = data?.swap || {};
    const disk = data?.disk || {};
    const la = data?.load || {};

    return (
        <div className="min-h-screen" style={{ background: C.bg, color: C.text }} data-testid="server-load-page">
            <Toaster position="bottom-right" toastOptions={{ style: { background: C.panel, border: `1px solid ${C.border}`, color: C.text } }} />
            <div className="sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(11,15,20,0.85)", borderBottom: `1px solid ${C.border}` }}>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center gap-3 flex-wrap">
                    <button onClick={onBack} className="btn-anim flex items-center gap-1.5 text-sm" style={{ color: C.sub }} data-testid="server-back-button">
                        <ArrowLeft size={16} /> Dashboard
                    </button>
                    <div className="h-6 w-px" style={{ background: C.border }} />
                    <Server size={16} color={C.accent} />
                    <span className="text-sm font-semibold">Server Load · Ubuntu Linux</span>
                    {data?.hostname && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: C.accent, background: "rgba(46,230,214,0.1)" }}>{data.hostname}</span>}
                    <div className="flex-1" />
                    <span className="text-[10px] font-mono" style={{ color: C.dim }}>uptime {fmtUptime(data?.uptime_sec)}</span>
                    <button onClick={load} className="btn-anim flex items-center gap-1 text-[11px]" style={{ color: C.sub }}><RefreshCw size={12} /> refresh</button>
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-5 space-y-4">
                {data && !data.available && (
                    <div className="panel p-4 text-sm" style={{ color: C.warn }}>
                        System metrics unavailable — /proc not found (non-Linux host).
                    </div>
                )}
                {err && <div className="panel p-4 text-sm" style={{ color: C.bear }}>Failed to load metrics: {String(err)}</div>}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Gauge label="CPU" pct={cpu.percent} sub={`${cpu.cores || "—"} cores`} />
                    <Gauge label="Memory" pct={mem.pct} sub={`${gb(mem.used)} / ${gb(mem.total)}`} />
                    <Gauge label="Disk /" pct={disk.pct} sub={`${gb(disk.used)} / ${gb(disk.total)}`} />
                    <Gauge label="Load 1m" pct={cpu.load_pct_1m} sub={la.load1 != null ? `${la.load1} (${cpu.cores}c)` : "—"} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ResRow icon={MemoryStick} label="Memory" pct={mem.pct}
                        detail={`used ${gb(mem.used)} · avail ${gb(mem.available)} · total ${gb(mem.total)}`} />
                    <ResRow icon={HardDrive} label="Disk /" pct={disk.pct}
                        detail={`used ${gb(disk.used)} · free ${gb(disk.free)} · total ${gb(disk.total)}`} />
                    <ResRow icon={Activity} label="Swap" pct={swap.pct}
                        detail={swap.total ? `used ${gb(swap.used)} · free ${gb(swap.free)} · total ${gb(swap.total)}` : "no swap configured"} />
                    <ResRow icon={Cpu} label="CPU" pct={cpu.percent}
                        detail={`${cpu.cores || "—"} cores · backend RSS ${gb(data?.backend_rss_bytes)}`} />
                </div>

                <div className="panel p-4" data-testid="sys-loadavg">
                    <div className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: C.sub }}>Load Average (per-core normalised)</div>
                    <div className="grid grid-cols-3 gap-4">
                        {[["1 min", la.load1], ["5 min", la.load5], ["15 min", la.load15]].map(([lbl, v]) => (
                            <div key={lbl}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[11px]" style={{ color: C.dim }}>{lbl}</span>
                                    <span className="font-mono text-sm" style={{ color: C.text }}>{v == null ? "—" : v.toFixed(2)}</span>
                                </div>
                                <Bar pct={v != null && cpu.cores ? (100 * v / cpu.cores) : 0} />
                            </div>
                        ))}
                    </div>
                    {la.procs_total != null && (
                        <div className="text-[11px] font-mono mt-3" style={{ color: C.dim }}>
                            processes: {la.procs_running} running / {la.procs_total} total
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
